import { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { submitBooking } from "../lib/api";
import usePageMeta from "../hooks/usePageMeta";

// ─────────────────────────────────────────────────────────────────
// Session Application - mirrors the legacy questionnaire so Cleopatra
// receives the exact context she needs to craft each session.
// ─────────────────────────────────────────────────────────────────

const RESIDENCE_OPTIONS = [
  "I reside in London",
  "I reside in the rest of the UK",
  "I reside in Europe",
  "I reside in North America",
  "I reside in Asia",
  "I reside in Australia / New Zealand",
  "I reside elsewhere",
];

const FIRST_HYP_AGE_OPTIONS = [
  "Under 12",
  "12 to 15",
  "16 to 18",
  "19 to 25",
  "26 to 35",
  "36 or older",
  "I had not heard of it before now",
];

const AGE_BRACKETS = ["21 to 30", "31 to 40", "41 to 50", "51 to 60", "60+"];
const GENDERS = ["Male", "Female", "Transgendered"];
const ORIENTATIONS = ["Straight", "Gay", "Bisexual", "Bi-curious"];
const YES_NO_NOTSURE = ["Yes", "No", "Not sure"];
const YES_NO = ["Yes", "No"];
const HANDED = ["Left handed", "Right handed", "Ambidextrous"];
const QUALITIES = [
  "Should she have a sense of humour?",
  "Should she adopt a serious attitude throughout?",
  "Should she have an intellectual approach?",
  "Should she be sensual in her approach?",
  "Should she be raunchy in her approach?",
];

const EMPTY = {
  full_name: "",
  email: "",
  residence: "",
  age_bracket: "",
  gender: "",
  orientation: "",
  first_hypnosis_age: "",
  been_hypnotised_before: "",
  awareness_source: "",
  experienced_eh: "",
  experienced_eh_details: "",
  session_hopes: "",
  handedness: "",
  fetishes_fantasies: "",
  qualities: [],
  limits: "",
  phobias: "",
  repugnant_smells: "",
  positive_memory: "",
  start_date: "",
  end_date: "",
  promo_code: "",
  how_discovered: "",
  additional_info: "",
  consent_18plus: false,
  consent_terms: false,
};

const DATE_RE = /^(0[1-9]|[12]\d|3[01])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/;

export default function Book() {
  usePageMeta({
    title: "Book a Session - Session Application",
    description:
      "Request a bespoke erotic hypnosis session with Queen Cleopatra. Complete the session application; every enquiry is read personally with a discreet reply within 48 hours.",
    path: "/book",
  });

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toggleQuality = (q) => {
    setForm((f) => {
      const has = f.qualities.includes(q);
      return { ...f, qualities: has ? f.qualities.filter((x) => x !== q) : [...f.qualities, q] };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent_18plus || !form.consent_terms) {
      toast.error("Please confirm 18+ and accept the terms.");
      return;
    }
    if (!DATE_RE.test(form.start_date) || !DATE_RE.test(form.end_date)) {
      toast.error("Please enter dates as dd/mm/yyyy.");
      return;
    }
    setLoading(true);
    try {
      const res = await submitBooking(form);
      toast.success(res.message || "Application received.");
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-book">
      {/* Editorial hero */}
      <section className="relative overflow-hidden border-b border-[#c9a04b]/15">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/ct5t5vid_erotic%20hypnosis%20UK%20pocket%20watch.jpg"
            alt="Champagne, rose and gold pocket watch - an evening with Cleopatra"
            className="w-full h-full object-cover object-center"
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(17,19,25,0.85) 0%, rgba(17,19,25,0.45) 55%, rgba(17,19,25,0.15) 100%)",
            }}
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-36">
          <div className="max-w-xl">
            <div className="eyebrow mb-5">Session Application</div>
            <h1 className="font-serif text-5xl md:text-7xl text-cream leading-[0.95]">
              Book a <em className="text-gold">Session</em>.
            </h1>
            <p className="mt-6 text-cream/80 leading-relaxed">
              Now that you know you have to see me, you need to request a session. Completing the
              questionnaire below is the first step. It does not guarantee an audience with me -
              but it is impossible to continue without it.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 md:px-10 lg:px-16 pb-24 pt-14">
        {done ? (
          <div
            data-testid="booking-success"
            className="border border-gold/40 p-10 bg-gold/5 text-center max-w-2xl mx-auto"
          >
            <h2 className="font-serif text-3xl text-cream mb-4">
              Your application has reached me.
            </h2>
            <p className="text-sm text-cream/75 leading-relaxed mb-6">
              I read every one personally. You can expect a discreet reply within 48 hours - often
              sooner. Should your dates not suit mine, I will propose alternatives.
            </p>
            <Link to="/" className="link-gold text-xs uppercase tracking-[0.2em]">
              ← Back to home
            </Link>
          </div>
        ) : (
          <>
            {/* Preamble from the original questionnaire */}
            <div className="prose-cream max-w-2xl mx-auto mb-12 text-cream/75 leading-relaxed space-y-5 text-[15px]">
              <p>
                I relish the thought, should you be granted a session with me, of guiding your
                hypnotic journey of fantasy and complete sexual fulfilment.
              </p>
              <p>
                We shall, if you pass this initial step, share many enchanting experiences as we
                spend much time together. This time will seem to be over in an instant, so powerful
                will be its impact on you. But the after-effects will persist for a while and be
                reinforced each time we meet. The questions that follow will shed light on your
                personality and experiences and help me to craft the hypnotic experience most
                suited to your tastes and desires.
              </p>
              <p>
                First, I'll want to discuss your prior experiences, your limits, your interests
                and your session desires. Open, willing participation in the session interview will
                greatly improve your experience. Remember that the more information you share with
                me, the greater will be the intensity of the experience we can share.{" "}
                <em className="text-gold">
                  I will never leave you with any debilitating post-hypnotic suggestions.
                </em>
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-8" data-testid="booking-form">
              {/* ── Contact ─────────────────────────────────── */}
              <Section title="Who you are">
                <TextField label="Your Name" required value={form.full_name} onChange={set("full_name")} testid="bk-name" />
                <TextField label="Your Email" type="email" required value={form.email} onChange={set("email")} testid="bk-email" help="Valid email required" />
                <Field label="Residence" required>
                  <select
                    required
                    value={form.residence}
                    onChange={set("residence")}
                    data-testid="bk-residence"
                    className="w-full bg-transparent border border-[#c9a04b]/35 text-cream px-3 py-3 text-sm focus:outline-none focus:border-gold"
                  >
                    <option value="" className="bg-charcoal">Select...</option>
                    {RESIDENCE_OPTIONS.map((o) => (
                      <option key={o} value={o} className="bg-charcoal">{o}</option>
                    ))}
                  </select>
                </Field>
              </Section>

              {/* ── Demographics ────────────────────────────── */}
              <Section title="About you">
                <RadioGroup label="How old are you?" name="age_bracket" value={form.age_bracket} onChange={set("age_bracket")} options={AGE_BRACKETS} testid="bk-age" />
                <RadioGroup label="Gender" name="gender" value={form.gender} onChange={set("gender")} options={GENDERS} testid="bk-gender" />
                <RadioGroup label="Your Sexual Orientation" name="orientation" value={form.orientation} onChange={set("orientation")} options={ORIENTATIONS} testid="bk-orient" />
                <RadioGroup label="Are you" name="handedness" value={form.handedness} onChange={set("handedness")} options={HANDED} testid="bk-handed" />
              </Section>

              {/* ── Hypnosis history ────────────────────────── */}
              <Section title="Your hypnosis history">
                <Field label="How old were you when you first learnt about hypnosis?" required>
                  <select
                    required
                    value={form.first_hypnosis_age}
                    onChange={set("first_hypnosis_age")}
                    data-testid="bk-first-age"
                    className="w-full bg-transparent border border-[#c9a04b]/35 text-cream px-3 py-3 text-sm focus:outline-none focus:border-gold"
                  >
                    <option value="" className="bg-charcoal">Select...</option>
                    {FIRST_HYP_AGE_OPTIONS.map((o) => (
                      <option key={o} value={o} className="bg-charcoal">{o}</option>
                    ))}
                  </select>
                </Field>
                <RadioGroup label="Have you been hypnotised before?" name="been_hyp" value={form.been_hypnotised_before} onChange={set("been_hypnotised_before")} options={YES_NO_NOTSURE} testid="bk-prev-hyp" />
                <TextareaField label="How did you first become aware of erotic hypnosis?" required rows={3} value={form.awareness_source} onChange={set("awareness_source")} testid="bk-awareness" />
                <RadioGroup label="Have you experienced erotic hypnosis?" name="exp_eh" value={form.experienced_eh} onChange={set("experienced_eh")} options={YES_NO} testid="bk-exp-eh" />
                <TextareaField label="If the answer to the above was yes, give details." rows={3} value={form.experienced_eh_details} onChange={set("experienced_eh_details")} testid="bk-exp-eh-details" />
              </Section>

              {/* ── Session desires ─────────────────────────── */}
              <Section title="Your session">
                <TextareaField
                  label="What are you hoping to experience in a session of erotic hypnosis?"
                  help="You can be as specific as you wish. Don't hold back - the more I know about you, the more you can get out of our sessions."
                  rows={5}
                  value={form.session_hopes}
                  onChange={set("session_hopes")}
                  testid="bk-hopes"
                />
                <TextareaField
                  label="Describe any fetishes or fantasies that you have, and how far you have gone to enact them."
                  rows={4}
                  value={form.fetishes_fantasies}
                  onChange={set("fetishes_fantasies")}
                  testid="bk-fetishes"
                />

                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-cream/55 block mb-3">
                    Tick the qualities you like in your ideal Female Hypnotist
                  </label>
                  <div className="space-y-2.5">
                    {QUALITIES.map((q) => (
                      <label key={q} className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={form.qualities.includes(q)}
                          onChange={() => toggleQuality(q)}
                          data-testid={`bk-q-${QUALITIES.indexOf(q)}`}
                          className="mt-1 accent-[#c9a04b]"
                        />
                        <span className="text-sm text-cream/80 group-hover:text-cream">{q}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <TextareaField
                  label="Describe any limits or no-go areas you have. This can be plain preference or health related."
                  rows={3}
                  value={form.limits}
                  onChange={set("limits")}
                  testid="bk-limits"
                />
                <TextareaField
                  label="Do you have any phobias or allergies that may interfere with a hypnotic session?"
                  rows={3}
                  value={form.phobias}
                  onChange={set("phobias")}
                  testid="bk-phobias"
                />
                <TextareaField
                  label="Are there any smells, e.g. perfume, that you find particularly repugnant?"
                  rows={2}
                  value={form.repugnant_smells}
                  onChange={set("repugnant_smells")}
                  testid="bk-smells"
                />
                <TextareaField
                  label="Describe a thought or memory that makes you feel warm, happy and generally positive."
                  rows={3}
                  value={form.positive_memory}
                  onChange={set("positive_memory")}
                  testid="bk-memory"
                />
              </Section>

              {/* ── Date range ──────────────────────────────── */}
              <Section title="Indicate a date range for when you would like your session">
                <p className="text-xs text-cream/55 -mt-2 mb-4 leading-relaxed">
                  Please suggest a window of dates that work for you. I will reply with the slots
                  I have free within your range - my diary is not published.
                </p>
                <div className="grid sm:grid-cols-2 gap-5">
                  <TextField
                    label="Start Date (dd/mm/yyyy)"
                    required
                    inputMode="numeric"
                    placeholder="dd/mm/yyyy"
                    value={form.start_date}
                    onChange={set("start_date")}
                    testid="bk-start-date"
                  />
                  <TextField
                    label="End Date (dd/mm/yyyy)"
                    required
                    inputMode="numeric"
                    placeholder="dd/mm/yyyy"
                    value={form.end_date}
                    onChange={set("end_date")}
                    testid="bk-end-date"
                  />
                </div>
              </Section>

              {/* ── Discovery & misc ────────────────────────── */}
              <Section title="A few last things">
                <TextField
                  label="If you have a promotional code please enter it here"
                  value={form.promo_code}
                  onChange={set("promo_code")}
                  testid="bk-promo"
                />
                <TextField
                  label="How did you discover me?"
                  value={form.how_discovered}
                  onChange={set("how_discovered")}
                  testid="bk-discovered"
                />
                <TextareaField
                  label="Any additional information worth sharing can go here."
                  rows={3}
                  value={form.additional_info}
                  onChange={set("additional_info")}
                  testid="bk-additional"
                />
              </Section>

              {/* ── Consent ─────────────────────────────────── */}
              <Section title="Consent">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.consent_18plus}
                    onChange={set("consent_18plus")}
                    data-testid="bk-c18"
                    required
                    className="mt-1 accent-[#c9a04b]"
                  />
                  <span className="text-xs text-cream/70">
                    I confirm I am 18 or over and consent to participate in erotic hypnosis.
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.consent_terms}
                    onChange={set("consent_terms")}
                    data-testid="bk-cterms"
                    required
                    className="mt-1 accent-[#c9a04b]"
                  />
                  <span className="text-xs text-cream/70">
                    I have read the <Link to="/legal/terms" className="link-gold">Terms</Link>,{" "}
                    <Link to="/legal/refund" className="link-gold">Refund</Link> and Privacy policy.
                  </span>
                </label>
              </Section>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full md:w-auto md:px-16 mt-4"
                data-testid="bk-submit"
              >
                {loading ? "Sending…" : "Submit application"}
              </button>
              <p className="text-[11px] text-cream/40 tracking-wider">
                No payment details collected on this site. Deposits are arranged discreetly by reply.
              </p>
            </form>
          </>
        )}
      </section>
    </div>
  );
}

// ─── Small presentational helpers ─────────────────────────────────

function Section({ title, children }) {
  return (
    <section className="border border-[#c9a04b]/15 bg-charcoalMuted/40 p-6 md:p-8">
      <div className="eyebrow mb-6">{title}</div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

function Field({ label, required, help, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] text-cream/55 block mb-2">
        {label} {required && <span className="text-gold normal-case tracking-normal ml-1">*</span>}
      </label>
      {children}
      {help && <div className="text-[11px] text-cream/40 mt-1.5">{help}</div>}
    </div>
  );
}

function TextField({ label, required, help, testid, ...rest }) {
  return (
    <Field label={label} required={required} help={help}>
      <input
        {...rest}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border border-[#c9a04b]/35 text-cream px-3 py-3 text-sm focus:outline-none focus:border-gold"
      />
    </Field>
  );
}

function TextareaField({ label, required, help, rows = 3, testid, value, onChange }) {
  return (
    <Field label={label} required={required} help={help}>
      <textarea
        rows={rows}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-transparent border border-[#c9a04b]/35 text-cream px-3 py-3 text-sm focus:outline-none focus:border-gold leading-relaxed"
      />
    </Field>
  );
}

function RadioGroup({ label, name, value, onChange, options, testid }) {
  return (
    <Field label={label}>
      <div className="flex flex-wrap gap-x-6 gap-y-2.5 pt-1">
        {options.map((o) => (
          <label key={o} className="flex items-center gap-2.5 cursor-pointer text-sm text-cream/80 hover:text-cream">
            <input
              type="radio"
              name={name}
              value={o}
              checked={value === o}
              onChange={onChange}
              data-testid={`${testid}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className="accent-[#c9a04b]"
            />
            {o}
          </label>
        ))}
      </div>
    </Field>
  );
}
